import { supabase } from '../lib/supabase';

export const authService = {
  async signUp(email, password, userData) {
    try {
      console.log('Starting signup process for:', email);
      console.log('User data being sent:', userData);
      
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${import.meta.env?.VITE_APP_URL || window.location?.origin}/login`,
          data: {
            full_name: userData?.fullName,
            phone: userData?.phone,
            role: userData?.role,
            avatar_url: userData?.profile_image || userData?.avatarUrl || '',
            business_name: userData?.businessName || '',
            service_description: userData?.serviceDescription || '',
            country: userData?.country || 'IE',
            currency: userData?.currency || 'EUR',
            // Include addresses (as JSON array) so the DB trigger can insert them on user creation
            addresses: userData?.addresses ?? []
          }
        }
      });

      console.log('Supabase signUp response:', { data, error });

      if (error) {
        console.error('SignUp error:', error);
        throw error;
      }

      // Verify user was actually created
      if (!data?.user) {
        console.error('No user object returned from signUp');
        throw new Error('Registration failed: No user created');
      }

      console.log('User created successfully:', data?.user?.id);

      // Check if email confirmation is required
      if (data?.user && !data?.session) {
        // Email confirmation is required - user will receive confirmation email
        console.log('Email confirmation required for:', email);
        return { 
          data: { 
            ...data, 
            requiresEmailConfirmation: true,
            message: 'Please check your email to confirm your account before logging in.'
          }, 
          error: null 
        };
      }

      console.log('Registration complete with active session');
      return { data, error: null };
    } catch (error) {
      console.error('SignUp exception:', error);
      return { data: null, error };
    }
  },

  async signIn(email, password) {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async signOut() {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  async updateProfile(updates) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('No user logged in');

      const { data, error } = await supabase?.from('user_profiles')?.update({
          full_name: updates?.fullName,
          phone: updates?.phone,
          avatar_url: updates?.avatarUrl
        })?.eq('id', user?.id)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getUserRole() {
    try {
      console.log('getUserRole: Starting...');
      const { data: { user } } = await supabase?.auth?.getUser();
      console.log('getUserRole: Current user:', user ? user?.id : 'null');
      
      if (!user) {
        console.error('getUserRole: No authenticated user');
        return null;
      }

      console.log('getUserRole: Querying user_profiles for user:', user?.id);
      const { data, error } = await supabase?.from('user_profiles')?.select('role')?.eq('id', user?.id)?.single();
      
      console.log('getUserRole: Query result:', { data, error: error ? error?.message : 'none' });

      if (error) {
        console.error('getUserRole: Database error:', error);
        throw error;
      }
      
      if (!data) {
        console.error('getUserRole: No profile found for user:', user?.id);
        return null;
      }
      
      console.log('getUserRole: Role found:', data?.role);
      return data?.role;
    } catch (error) {
      console.error('getUserRole: Exception:', error);
      return null;
    }
  },

  async resetPassword(email) {
    try {
      const { data, error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${import.meta.env?.VITE_APP_URL || window.location?.origin}/reset-password`
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updatePassword(newPassword) {
    try {
      const { data, error } = await supabase?.auth?.updateUser({
        password: newPassword
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};